/**
 * Register the Alpine.js `emailComposeForm` component for email recipient
 * management with Contact/User autocomplete.
 *
 * Text left in a field never drops out of a send. Leaving the field commits a valid
 * address, and the form's submit commits whatever is left or stops with an error.
 *
 * Usage in ERB:
 *   <div x-data="emailComposeForm(['john@example.com'], ['jane@example.com'])">
 */
import { focusOnceShown } from "./compose_helpers.js";

const FIELDS = ["to", "cc", "bcc"];

export default function registerEmailCompose(Alpine) {
  Alpine.data("emailComposeForm", (initialTo = [], initialCc = []) => ({
    toAddresses: [...initialTo],
    ccAddresses: [],
    bccAddresses: [],
    toInput: "",
    ccInput: "",
    bccInput: "",
    showCc: false,
    showBcc: false,
    invalidField: null, // the field a submit stopped on

    // Autocomplete state
    suggestions: [],
    activeField: null, // "to" | "cc" | "bcc" | null
    activeIndex: -1,
    searchLoading: false,
    _searchSeq: 0,
    _debounceTimer: null,
    _blurTimer: null,

    init() {
      this.toAddresses = [...initialTo];
      this.ccAddresses = [...initialCc];
      if (this.ccAddresses.length > 0) {
        this.showCc = true;
      }
      this.$el.closest("form").addEventListener("submit", (event) => this.commitPending(event));
    },

    revealCc() {
      this.showCc = true;
      focusOnceShown(this, this.$refs.ccInput);
    },

    addAddress(field) {
      const inputRef = `${field}Input`;
      const value = this[inputRef].trim().replace(/,+$/, "").trim();
      if (!value) return false;
      if (!this.isValidEmail(value)) return false;
      this._pushEmail(field, value);
      this[inputRef] = "";
      return true;
    },

    // Tab stays in the field only to commit an address. Anything else has to let it
    // through: keyboard users have no other way out of the field.
    commitOnTab(field, event) {
      if (this.addAddress(field)) event.preventDefault();
    },

    // The hidden inputs render a tick after an address is added, and the form reads them
    // when it submits, so a commit here has to submit again.
    commitPending(event) {
      let committed = false;
      for (const field of FIELDS) {
        if (!this[`${field}Input`].trim()) continue;
        if (!this.addAddress(field)) {
          event.preventDefault();
          this.invalidField = field;
          this.$refs[`${field}Input`].focus();
          return;
        }
        committed = true;
      }
      if (!committed) return;
      event.preventDefault();
      this.$nextTick(() => event.target.requestSubmit(event.submitter));
    },

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    _pushEmail(field, email) {
      const list = this[`${field}Addresses`];
      if (!list.includes(email)) {
        list.push(email);
      }
    },

    onInput(field) {
      this.activeField = field;
      if (this.invalidField === field) this.invalidField = null;
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this.search(field), 200);
    },

    async search(field) {
      const inputRef = `${field}Input`;
      const query = (this[inputRef] || "").trim();
      if (query.length < 2) {
        this.suggestions = [];
        this.activeIndex = -1;
        return;
      }
      const seq = ++this._searchSeq;
      this.searchLoading = true;
      try {
        const url = `/email_messages/recipient_search?q=${encodeURIComponent(query)}`;
        const resp = await fetch(url, { headers: { Accept: "application/json" } });
        if (!resp.ok) {
          this.suggestions = [];
          this.activeIndex = -1;
          return;
        }
        const data = await resp.json();
        if (seq !== this._searchSeq) return;
        this.suggestions = data.results || [];
        this.activeIndex = this.suggestions.length > 0 ? 0 : -1;
      } finally {
        if (seq === this._searchSeq) this.searchLoading = false;
      }
    },

    selectSuggestion(idx) {
      const item = this.suggestions[idx];
      if (!item || !this.activeField) return;
      this._pushEmail(this.activeField, item.email);
      const inputRef = `${this.activeField}Input`;
      this[inputRef] = "";
      this.suggestions = [];
      this.activeIndex = -1;
    },

    onKeyDown(field, event) {
      // If there are no suggestions yet, fall through to the existing tag
      // commit handlers (enter/tab/comma) — but if the dropdown is open with
      // an active item, Enter selects it instead.
      if (this.activeField !== field) return false;
      if (event.key === "Escape") return this._dismissSuggestions(event);
      if (this.suggestions.length === 0) return false;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        this.activeIndex = (this.activeIndex + 1) % this.suggestions.length;
        return true;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        this.activeIndex = (this.activeIndex - 1 + this.suggestions.length) % this.suggestions.length;
        return true;
      }
      if (event.key === "Enter" && this.activeIndex >= 0) {
        event.preventDefault();
        this.selectSuggestion(this.activeIndex);
        return true;
      }
      return false;
    },

    // Escape closes the innermost thing open. While the list or its loading row
    // shows, that is the list: the key must not reach the modal around the composer.
    // With nothing showing it carries on and closes the modal.
    _dismissSuggestions(event) {
      if (this.suggestions.length === 0 && !this.searchLoading) return false;
      event.preventDefault();
      event.stopPropagation();
      clearTimeout(this._debounceTimer);
      this._searchSeq++; // an answer still on its way must not reopen the list
      this.searchLoading = false;
      this.suggestions = [];
      this.activeIndex = -1;
      return true;
    },

    // Picking a suggestion never lands here: its mousedown keeps the focus in the field.
    onBlur(field) {
      this.addAddress(field);
      clearTimeout(this._blurTimer);
      this._blurTimer = setTimeout(() => {
        this.suggestions = [];
        this.activeIndex = -1;
      }, 150);
    }
  }));
}
